// Client-side CSV export for the Analysis charts. Each chart hands over the
// same rows it plots, so the file holds exactly what the chart draws — no
// second computation that could drift from the picture.

export type CsvValue = string | number | boolean | null | undefined;

export interface CsvColumn<Row = Record<string, CsvValue>> {
  header: string;
  /** A row key, or a function for values the row only carries indirectly. */
  value: keyof Row | ((row: Row) => CsvValue);
}

/** Strip binary float noise (71.60000000000001) without rounding real data. */
const formatNumber = (n: number): string =>
  Number.isFinite(n) ? String(Number(n.toPrecision(12))) : '';

const formatCell = (v: CsvValue): string => {
  if (v === null || v === undefined) return '';
  const text = typeof v === 'number' ? formatNumber(v) : String(v);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const toCsv = <Row>(rows: Row[], columns: CsvColumn<Row>[]): string => {
  const cell = (row: Row, c: CsvColumn<Row>): CsvValue =>
    typeof c.value === 'function'
      ? c.value(row)
      : (row as Record<string, CsvValue>)[c.value as string];
  const lines = [
    columns.map((c) => formatCell(c.header)).join(','),
    ...rows.map((row) => columns.map((c) => formatCell(cell(row, c))).join(',')),
  ];
  return `${lines.join('\r\n')}\r\n`;
};

/** Filename-safe: letters, digits, dot, dash; everything else collapses to '-'. */
const slug = (s: string): string =>
  s.trim().replace(/[^A-Za-z0-9.-]+/g, '-').replace(/^-+|-+$/g, '');

export const csvFilename = (parts: (string | null | undefined)[]): string =>
  `${parts.filter((p): p is string => !!p && slug(p) !== '').map(slug).join('_')}.csv`;

export const downloadCsv = (filename: string, csv: string): void => {
  // The BOM makes Excel read the file as UTF-8, so '°F' and '—' in the
  // headers survive a double-click open.
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
